"use client"

import { useState, useEffect } from "react"
import { Button } from "../../components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card"
import { Link } from "react-router-dom"
import { CalendarDays, Clock, Download, Music, Palette, ScrollText, Users } from "lucide-react"
import Sidebar from "./sidebar"

interface AdmissionProcedure {
  id: number
  start: string
  end: string
  start_zapis: string
  end_zapis: string
  obor: string
  dny: Array<{
    id: number
    pid: number
    mesto: string
    datum: string
    casy: Array<{
      id: number
      pid: number
      did: number
      od: string
      do: string
      zabrano: number
      type: string
      zabrano_data?: string
    }>
  }>
}

// Default colors for each field
const DEFAULT_COLORS = {
  hudebni: '#4f7971',
  tanecni: '#96522b',
  vytvarny: '#d4b682'
}

const oborIcons = {
  hudebni: <Music className="w-5 h-5" />,
  tanecni: <Users className="w-5 h-5" />,
  vytvarny: <Palette className="w-5 h-5" />,
}

export function ExportPrijPage() {
  const [activeTab, setActiveTab] = useState<"hudebni" | "tanecni" | "vytvarny">("hudebni")
  const [admissions, setAdmissions] = useState<AdmissionProcedure[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showUserDetails, setShowUserDetails] = useState(false)

  useEffect(() => {
    const token = document.cookie.split("; ").find(row => row.startsWith("token"))
    if (token === undefined) {
      window.location.href = "/adminlogin"
      return
    }
    fetchAdmissions()
  }, [activeTab])

  const fetchAdmissions = async () => {
    try {
      setLoading(true)
      const response = await fetch(`https://preview.zushm.cz/api/prihlasky/prijmacirizeni?obor=${activeTab}`)
      if (!response.ok) {
        throw new Error("Network response was not ok")
      }
      const data = await response.json()
      setAdmissions(Array.isArray(data) ? data : [data])
      setError(null)
    } catch (error) {
      console.error("Error fetching admissions:", error)
      setError("Nepodařilo se načíst data o přijímacích řízeních")
      setAdmissions([])
    } finally {
      setLoading(false)
    }
  }

  const handleExport = async (id: number) => {
    try {
      const token = document.cookie.split("; ").find(row => row.startsWith("token"))
      if (!token) {
        throw new Error("No authentication token found")
      }
      const tokenValue = token.split("=")[1]
      
      const response = await fetch("https://preview.zushm.cz/api/prihlasky/prijmacirizeni/export", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": tokenValue,
        },
        body: JSON.stringify({ id }),
      })
  
      if (!response.ok) {
        throw new Error("Export failed")
      }
  
      const contentDisposition = response.headers.get("Content-Disposition")
      const filename = contentDisposition
        ? contentDisposition.split("filename=")[1].replace(/['"]/g, "")
        : `prijimaci-rizeni-${id}.csv`
  
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (error) {
      console.error("Error exporting admission:", error)
      alert("Export se nezdařil")
    }
  }

  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }
    return new Date(dateString).toLocaleString('cs-CZ', options)
  }

  const parseRegistrationData = (data?: string) => {
    if (!data) return [];
    return data.split(';').map(entry => {
      const parts = entry.split(',').map(part => part.trim());
      // For "hudebni" field, the format is: jmeno, mobil, email, datum, nastroj
      if (activeTab === 'hudebni' && parts.length >= 5) {
        return {
          name: parts[0],
          phone: parts[1],
          email: parts[2],
          date: parts[3],
          instrument: parts[4] // Add instrument field
        };
      }
      // For other fields, the format is: jmeno, mobil, email, datum
      return {
        name: parts[0],
        phone: parts[1],
        email: parts[2],
        date: parts[3],
        instrument: '' // Empty for non-music fields
      };
    }).filter(reg => reg.name);
  }

  const getStatusDisplay = (count: number) => {
    if (activeTab === 'vytvarny') {
      if (count === 0) return { text: 'Volné místo', color: 'bg-green-100 text-green-800' }
      if (count <= 5) return { text: 'Nízký zájem', color: 'bg-blue-100 text-blue-800' }
      if (count <= 15) return { text: 'Střední zájem', color: 'bg-orange-100 text-orange-800' }
      return { text: 'Vysoký zájem', color: 'bg-red-100 text-red-800' }
    } else {
      return {
        text: count > 0 ? 'Obsazeno' : 'Volno',
        color: count > 0 ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
      }
    }
  }

  if (error) {
    return (
      <div className="flex min-h-screen">
        <Sidebar />
        <div className="flex-1 p-4">
          <div className="flex items-center gap-4 mb-6">
          </div>
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
            {error}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <div className="flex-1 p-4 md:p-6 overflow-auto">
        {/* Combined header with back button, tabs, and toggle */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            
            {/* Tabs Navigation */}
            <div className="overflow-x-auto">
              <div className="flex border-b w-max min-w-full">
                {(["hudebni", "tanecni", "vytvarny"] as const).map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-6 py-3 text-lg font-medium flex items-center gap-3 flex-shrink-0 ${activeTab === tab ? "border-b-2 text-white" : "text-muted-foreground"}`}
                    style={{ 
                      backgroundColor: activeTab === tab ? DEFAULT_COLORS[tab] : 'transparent',
                      borderBottomColor: activeTab === tab ? DEFAULT_COLORS[tab] : undefined 
                    }}
                  >
                    {oborIcons[tab]}
                    {tab === "hudebni" && "Hudební"}
                    {tab === "tanecni" && "Taneční"}
                    {tab === "vytvarny" && "Výtvarný"}
                  </button>
                ))}
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <label className="flex items-center cursor-pointer">
              <div className="relative">
                <input 
                  type="checkbox" 
                  className="sr-only" 
                  checked={showUserDetails}
                  onChange={() => setShowUserDetails(!showUserDetails)}
                />
                <div className={`block w-14 h-8 rounded-full ${showUserDetails ? 'bg-blue-500' : 'bg-gray-600'}`}></div>
                <div className={`dot absolute left-1 top-1 bg-white w-6 h-6 rounded-full transition ${showUserDetails ? 'transform translate-x-6' : ''}`}></div>
              </div>
              <div className="ml-3 text-gray-700 font-medium">
                Zobrazit detaily účastníků
              </div>
            </label>
          </div>
        </div>

        {loading ? (
          <div className="space-y-6">
            {[...Array(3)].map((_, i) => (
              <Card key={i} className="p-6">
                <CardHeader>
                  <div className="h-8 w-[300px] bg-gray-200 rounded animate-pulse" />
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="h-5 w-[350px] bg-gray-200 rounded animate-pulse" />
                  <div className="h-5 w-[350px] bg-gray-200 rounded animate-pulse" />
                  <div className="h-5 w-[350px] bg-gray-200 rounded animate-pulse" />
                  <div className="h-5 w-[350px] bg-gray-200 rounded animate-pulse" />
                  <div className="h-12 w-[150px] bg-gray-200 rounded animate-pulse mt-6" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : admissions.length > 0 ? (
          <div className="space-y-8">
            {admissions.map((admission) => (
              <Card key={admission.id} className="hover:shadow-lg transition-shadow p-6">
                <CardHeader className="pb-6">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <CardTitle className="flex items-center gap-3 text-2xl">
                      <span>Přijímací řízení #{admission.id}</span>
                    </CardTitle>
                    <Button 
                      onClick={() => handleExport(admission.id)} 
                      variant="outline"
                      className="gap-2 text-lg h-12 px-6"
                    >
                      <Download className="w-5 h-5" /> Exportovat
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    <div className="space-y-4">
                      <div className="flex items-center gap-3 text-base">
                        <ScrollText className="w-5 h-5 text-muted-foreground" />
                        <span className="text-lg">{admission.obor}</span>
                      </div>
                      
                      <div className="flex items-center gap-3 text-base">
                        <CalendarDays className="w-5 h-5 text-muted-foreground" />
                        <span className="text-lg">
                          <strong>Období:</strong> {formatDate(admission.start)} - {formatDate(admission.end)}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-3 text-base">
                        <Clock className="w-5 h-5 text-muted-foreground" />
                        <span className="text-lg">
                          <strong>Zápis:</strong> {formatDate(admission.start_zapis)} - {formatDate(admission.end_zapis)}
                        </span>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-medium flex items-center gap-3 mb-4 text-xl">
                        <Users className="w-5 h-5" /> Termíny:
                      </h4>
                      
                      {/* Mobile view */}
                      <div className="md:hidden space-y-4">
                        {admission.dny?.map((den) => (
                          <div key={den.id} className="border rounded-lg p-4">
                            <div className="font-medium text-lg">{den.mesto}</div>
                            <div className="text-base text-muted-foreground">
                              {new Date(den.datum).toLocaleDateString('cs-CZ')}
                            </div>
                            {den.casy?.length > 0 && (
                              <ul className="mt-3 space-y-3">
                                {den.casy?.map((cas) => {
                                  const status = getStatusDisplay(cas.zabrano)
                                  return (
                                    <li key={cas.id} className="text-base">
                                      <div className="flex flex-col gap-2">
                                        <div className="flex justify-between items-start">
                                          {activeTab === 'vytvarny' ? (
                                            <div className="font-medium">
                                              Počet přihlášených: {cas.zabrano}
                                            </div>
                                          ) : (
                                            <div>
                                              <span className="font-medium">{cas.od} - {cas.do}</span> ({cas.type})
                                            </div>
                                          )}
                                          <span className={`inline-block text-sm px-3 py-1 rounded-full ${status.color}`}>
                                            {status.text}
                                          </span>
                                        </div>
                                        
                                        {showUserDetails && cas.zabrano_data && (
                                          <div className="space-y-2">
                                            <div className="font-medium">Přihlášení účastníci:</div>
                                            {parseRegistrationData(cas.zabrano_data).map((reg, i) => (
                                              <div key={i} className="text-sm pl-2 border-l-2 border-gray-200">
                                                <div><strong>Jméno:</strong> {reg.name}</div>
                                                <div><strong>Telefon:</strong> {reg.phone}</div>
                                                <div><strong>Email:</strong> {reg.email}</div>
                                                {activeTab === 'hudebni' && reg.instrument && (
                                                  <div><strong>Nástroj:</strong> {reg.instrument}</div>
                                                )}
                                                <div><strong>Datum přihlášení:</strong> {reg.date}</div>
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    </li>
                                  )
                                })}
                              </ul>
                            )}
                          </div>
                        ))}
                      </div>
                      
                      {/* Desktop view */}
                      <div className="hidden md:block">
                        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                          {admission.dny?.map((den) => (
                            <div key={den.id} className="border rounded-lg p-4">
                              <div className="font-medium text-lg">{den.mesto}</div>
                              <div className="text-base text-muted-foreground mb-3">
                                {new Date(den.datum).toLocaleDateString('cs-CZ')}
                              </div>
                              {den.casy?.length > 0 ? (
                                <ul className="space-y-3">
                                  {den.casy?.map((cas) => {
                                    const status = getStatusDisplay(cas.zabrano)
                                    return (
                                      <li key={cas.id} className="text-base">
                                        <div className="flex flex-col gap-2">
                                          <div className="flex justify-between items-start">
                                            {activeTab === 'vytvarny' ? (
                                              <div className="font-medium">
                                                Počet přihlášených: {cas.zabrano}
                                              </div>
                                            ) : (
                                              <div>
                                                <span className="font-medium">{cas.od} - {cas.do}</span> ({cas.type})
                                              </div>
                                            )}
                                            <span className={`inline-block text-sm px-3 py-1 rounded-full ${status.color}`}>
                                              {status.text}
                                            </span>
                                          </div>
                                          
                                          {showUserDetails && cas.zabrano_data && (
                                            <div className="space-y-2">
                                              <div className="font-medium">Přihlášení účastníci:</div>
                                              {parseRegistrationData(cas.zabrano_data).map((reg, i) => (
                                                <div key={i} className="text-sm pl-2 border-l-2 border-gray-200">
                                                  <div><strong>Jméno:</strong> {reg.name}</div>
                                                  <div><strong>Telefon:</strong> {reg.phone}</div>
                                                  <div><strong>Email:</strong> {reg.email}</div>
                                                  {activeTab === 'hudebni' && reg.instrument && (
                                                    <div><strong>Nástroj:</strong> {reg.instrument}</div>
                                                  )}
                                                  <div><strong>Datum přihlášení:</strong> {reg.date}</div>
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      </li>
                                    )
                                  })}
                                </ul>
                              ) : (
                                <div className="text-muted-foreground">Žádné časy</div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-muted-foreground mb-6 text-xl">Žádná přijímací řízení nenalezena</div>
            <Button variant="outline" onClick={fetchAdmissions} className="text-lg h-12 px-8">
              Zkusit znovu
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}

export default ExportPrijPage